import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { M } from "./tokens";
import { PROOF } from "./machines";
import { ADS, CREATIVE_ADS, frameSrc, fallbackTo } from "./assets";
import { FullScreenEmbed } from "./FullScreenEmbed";

const STATUS = { ok: M.ok, warn: M.warn, danger: M.danger, dim: M.dim };

function stagger(i, step) {
  return {
    animation: `sixIn 420ms cubic-bezier(0.16,1,0.3,1) ${i * 55}ms both`,
  };
}

function Badge({ v }) {
  if (typeof v !== "object") return <span className="f-mono text-[11px]" style={{ color: M.muted }}>{v}</span>;
  const c = STATUS[v.s] || M.muted;
  return (
    <span className="f-mono inline-block rounded px-2 py-[3px] text-[9px] uppercase tracking-[0.1em]" style={{ color: c, border: `1px solid ${c}55`, background: `${c}12` }}>
      {v.t}
    </span>
  );
}

/* ── table of rows ── */
export function RowsPanel({ data, step, accent }) {
  return (
    <div data-testid="panel-rows">
      <div className="hidden gap-3 border-b pb-2 sm:grid" style={{ borderColor: M.border, gridTemplateColumns: `2.2fr repeat(${data.head.length - 1}, 1fr)` }}>
        {data.head.map((h) => (
          <div key={h} className="f-mono text-[8.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>{h}</div>
        ))}
      </div>
      {data.rows.map((r, i) => (
        <div
          key={i}
          className="grid gap-3 border-b py-2.5"
          style={{ borderColor: M.border, gridTemplateColumns: `2.2fr repeat(${data.head.length - 1}, 1fr)`, ...stagger(i, step) }}
        >
          {r.map((c, k) => (
            <div key={k} className={k === 0 ? "text-[12.5px] leading-snug" : "f-mono tnum text-[11.5px]"} style={{ color: k === 0 ? M.ink : M.muted }}>
              {typeof c === "object" ? <Badge v={c} /> : c}
            </div>
          ))}
        </div>
      ))}
      {data.note && (
        <p className="mt-3 text-[12px] leading-relaxed" style={{ color: accent }}>{data.note}</p>
      )}
    </div>
  );
}

/* ── grid of produced ad frames ── */
export function FramesPanel({ data, step, accent }) {
  /* the creative machine has its own pool of produced statics */
  const pool = data.frames.length === 4 ? CREATIVE_ADS : ADS;
  /* the eight real Axos statics get a 4 across board with true 3:4 frames */
  const board = data.frames.length === 8;
  return (
    <div data-testid="panel-frames">
      <div className="f-mono mb-3 text-[9px] uppercase tracking-[0.16em]" style={{ color: accent }}>{data.label}</div>
      <div className={board ? "grid grid-cols-2 gap-2.5 sm:grid-cols-4" : "grid gap-2.5 sm:grid-cols-3"}>
        {data.frames.map((f, i) => (
          <div
            key={i}
            className="flex flex-col justify-between overflow-hidden rounded-lg border p-2.5"
            style={{ borderColor: M.border, background: M.surface2, minHeight: board ? 0 : 118, ...stagger(i, step) }}
          >
            {f.img || pool[i] ? (
              <div
                className={board ? "mb-2 overflow-hidden rounded" : "mb-2 flex h-[72px] items-center justify-center overflow-hidden rounded"}
                style={{ background: `linear-gradient(135deg, ${accent}1c, ${accent}06)`, border: `1px solid ${accent}22`, aspectRatio: board ? "3 / 4" : undefined }}
              >
                <img
                  src={frameSrc(f.img, i, pool)}
                  onError={(e) => fallbackTo(e, f.img)}
                  alt={f.angle}
                  loading="lazy"
                  className={board ? "h-full w-full object-cover" : "h-full w-full object-contain"}
                  style={{ padding: board ? 0 : 4 }}
                />
              </div>
            ) : (
              <div className="mb-2 h-[34px] rounded" style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}08)`, border: `1px solid ${accent}22` }} />
            )}
            <div className="text-[11.5px] leading-snug" style={{ color: M.ink }}>{f.hook}</div>
            <div className="mt-2 flex items-center justify-between">
              <span className="f-mono text-[8px] uppercase tracking-[0.1em]" style={{ color: accent }}>{f.angle}</span>
              <span className="f-mono text-[8px]" style={{ color: M.dim }}>{f.src}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── action log ── */
export function LogPanel({ data, step }) {
  return (
    <div data-testid="panel-log">
      {data.map((l, i) => {
        const c = STATUS[l.k] || M.muted;
        return (
          <div key={i} className="flex items-start gap-3 border-b py-2.5 last:border-b-0" style={{ borderColor: M.border, ...stagger(i, step) }}>
            <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: c }} />
            <span className="f-mono w-[74px] shrink-0 text-[9px] uppercase tracking-[0.1em]" style={{ color: c }}>{l.t}</span>
            <span className="min-w-0 flex-1 text-[12.5px] leading-snug" style={{ color: l.k === "dim" ? M.muted : M.ink }}>{l.d}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── klaviyo flow tree with a split at each stage ── */
export function TreePanel({ data, step, accent }) {
  return (
    <div data-testid="panel-tree">
      <div className="f-mono mb-4 text-[9px] uppercase tracking-[0.16em]" style={{ color: accent }}>{data.title}</div>
      {data.stages.map((s, i) => (
        <div key={s.n} style={stagger(i, step)}>
          <div className="flex items-center gap-2">
            <span className="f-mono text-[9px] uppercase tracking-[0.12em]" style={{ color: M.dim }}>{s.n}</span>
            <span className="h-px flex-1" style={{ background: M.border }} />
            <span className="f-mono text-[8.5px] uppercase tracking-[0.1em]" style={{ color: accent }}>testing: {s.test}</span>
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {[["A", s.a], ["B", s.b]].map(([k, v]) => (
              <div key={k} className="flex items-start gap-2 rounded-lg border p-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
                <span className="f-mono text-[9px]" style={{ color: accent }}>{k}</span>
                <span className="text-[11.5px] leading-snug" style={{ color: M.ink }}>{v}</span>
              </div>
            ))}
          </div>
          {i < data.stages.length - 1 && <div className="mx-auto my-2 h-4 w-px" style={{ background: M.border }} />}
        </div>
      ))}
    </div>
  );
}

/* ── PDP wireframe: control vs variant ── */
function WireBlock({ label, changed, i, step, accent }) {
  return (
    <div
      className="mb-1.5 rounded px-2.5 py-2 text-[10.5px]"
      style={{
        border: `1px ${changed ? "solid" : "dashed"} ${changed ? accent : M.border}`,
        background: changed ? `${accent}12` : "transparent",
        color: changed ? accent : M.muted,
        ...stagger(i, step),
      }}
    >
      {label}
      {changed && <span className="f-mono float-right text-[8px] uppercase tracking-[0.1em]">changed</span>}
    </div>
  );
}

export function WirePanel({ data, step, accent }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="panel-wire">
      <div>
        <div className="f-mono mb-2.5 text-[9px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>Control</div>
        {data.control.map((b, i) => (
          <WireBlock key={b} label={b} i={i} step={step} accent={accent} changed={false} />
        ))}
      </div>
      <div>
        <div className="f-mono mb-2.5 text-[9px] uppercase tracking-[0.16em]" style={{ color: accent }}>Variant · staged draft</div>
        {data.variant.map((b, i) => (
          <WireBlock key={b} label={b} i={i} step={step} accent={accent} changed={data.changed.includes(b)} />
        ))}
      </div>
    </div>
  );
}

/* ── the real page, running: small preview, click to full screen ── */
export function LivePanel({ data, accent }) {
  const [tab, setTab] = useState(0);
  const [full, setFull] = useState(false);
  const f = data.frames[Math.min(tab, data.frames.length - 1)];
  const native = f.w || 1280;
  const thumbW = 224;
  const thumbH = 318;
  const scale = thumbW / native;

  return (
    <div data-testid="panel-live">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
        <span className="f-mono text-[8.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>{data.label}</span>
        {data.frames.length > 1 && (
          <div className="ml-auto flex gap-1">
            {data.frames.map((fr, i) => (
              <button
                key={fr.key}
                onClick={() => setTab(i)}
                data-testid={`live-tab-${fr.key}`}
                className="focus-ring f-mono rounded-full px-2.5 py-1 text-[8.5px] uppercase tracking-[0.1em] transition-colors"
                style={{
                  color: i === tab ? accent : M.dim,
                  border: `1px solid ${i === tab ? `${accent}66` : M.border}`,
                  background: i === tab ? `${accent}12` : "transparent",
                }}
              >
                {fr.tab || fr.key}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-5 p-5 sm:flex-row">
        <button
          data-testid={`live-thumb-${f.key}`}
          onClick={() => setFull(true)}
          aria-label={`Open ${f.title} full screen`}
          className="focus-ring relative shrink-0 overflow-hidden rounded-lg border transition-transform hover:-translate-y-[2px]"
          style={{ width: thumbW, height: thumbH, borderColor: M.border, background: "#FDFBF7" }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <iframe
              key={f.key}
              title={f.title}
              src={`${f.route}?embed=1`}
              loading="lazy"
              scrolling="no"
              tabIndex={-1}
              style={{
                width: native,
                height: Math.ceil(thumbH / scale),
                border: 0,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                pointerEvents: "none",
              }}
            />
          </div>
        </button>
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <span className="f-mono text-[8.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>{f.url}</span>
          <div className="f-grotesk mt-2 max-w-[26ch] text-[19px] font-semibold leading-snug">{f.title}</div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => setFull(true)}
              data-testid="live-fullscreen-open"
              className="focus-ring f-mono rounded px-4 py-2.5 text-[9px] font-semibold uppercase tracking-[0.12em]"
              style={{ background: accent, color: "#0A0C10" }}
            >
              Full screen
            </button>
            <Link
              to={`${f.route}?tests=1`}
              data-testid={`live-open-${f.key}`}
              className="focus-ring f-mono rounded px-4 py-2.5 text-[9px] uppercase tracking-[0.12em]"
              style={{ border: `1px solid ${M.border}`, color: M.ink }}
            >
              Walk it
            </Link>
          </div>
        </div>
      </div>
      {full && <FullScreenEmbed route={f.route} label={f.url} onClose={() => setFull(false)} />}
    </div>
  );
}

/* ── standing-job calendar ── */
export function CalendarPanel({ data, step, accent }) {
  return (
    <div data-testid="panel-cal">
      <div className="f-mono mb-3 flex flex-wrap items-center gap-2 text-[9px] uppercase tracking-[0.16em]" style={{ color: accent }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
        {data.cadence}
      </div>
      {data.items.map(([d, t, who], i) => (
        <div
          key={d}
          className="flex items-center gap-3 border-b py-2.5 last:border-b-0"
          style={{ borderColor: M.border, ...stagger(i, step) }}
        >
          <span className="f-mono w-9 shrink-0 text-[9.5px] tracking-[0.12em]" style={{ color: M.dim }}>{d}</span>
          <span className="min-w-0 flex-1 text-[12.5px] leading-snug" style={{ color: M.ink }}>{t}</span>
          <span
            className="f-mono shrink-0 rounded px-2 py-[3px] text-[8px] uppercase tracking-[0.1em]"
            style={{
              color: who === "human" ? M.warn : M.ok,
              border: `1px solid ${who === "human" ? M.warn : M.ok}55`,
              background: who === "human" ? `${M.warn}12` : `${M.ok}10`,
            }}
          >
            {who === "human" ? "you" : "machine"}
          </span>
        </div>
      ))}
    </div>
  );
}
